import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { servicesList, serviceStandards } from "../../mocks/servicesData";

export default function Services() {
  const { hash } = useLocation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const hashId = hash.slice(1);
    if (hashId) {
      setExpandedId(hashId);
      setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 400);
    }
  }, [hash]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Soft abstract green and beige organic shapes pattern, elegant background with warm neutral tones and emerald green accents, calming professional atmosphere, minimal elegant design, no text, gentle gradients"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Non-Medical Home Care
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            From personal care to companionship, we offer a full range of non-medical home care services designed to help you or your loved one live safely and comfortably at home.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="services-grid" ref={setRef("services-grid")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("services-grid", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              What We Provide
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Care Tailored to Your Needs
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Each service is delivered with compassion, professionalism, and a deep respect for your dignity and independence.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {servicesList.map((service, i) => {
              const isExpanded = expandedId === service.id;
              return (
                <div
                  id={service.id}
                  key={service.id}
                  className={`bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-brand-green-200 transition-all duration-300 ${
                    isVisible["services-grid"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                    {/* Image */}
                    <div className="md:col-span-1 relative h-48 md:h-auto">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="md:col-span-2 p-5 md:p-8">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-brand-green-50 flex items-center justify-center shrink-0">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${service.icon} text-lg text-brand-green-600`} />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">{service.shortDescription}</p>
                        </div>
                      </div>

                      {/* Expandable Details */}
                      <div
                        className={`overflow-hidden transition-all duration-500 ${
                          isExpanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="pt-4 space-y-5">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Full Description</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">{service.fullDescription}</p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-check-line text-brand-green-600 text-xs" />
                                </div>
                                Benefits
                              </h4>
                              <ul className="space-y-1.5">
                                {service.benefits.map((benefit) => (
                                  <li key={benefit} className="flex items-start gap-2 text-xs text-gray-500">
                                    <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 shrink-0">
                                      <i className="ri-checkbox-circle-line text-brand-green-500 text-xs" />
                                    </div>
                                    {benefit}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-user-line text-brand-green-600 text-xs" />
                                </div>
                                Who It Is For
                              </h4>
                              <p className="text-xs text-gray-500 leading-relaxed">{service.whoIsItFor}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : service.id)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-green-600 hover:text-brand-green-700 transition-colors"
                        >
                          {isExpanded ? "Show Less" : "Learn More"}
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={isExpanded ? "ri-arrow-up-s-line text-sm" : "ri-arrow-down-s-line text-sm"} />
                          </div>
                        </button>
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-brand-green-600 text-white rounded-full hover:bg-brand-green-700 transition-all whitespace-nowrap"
                        >
                          Request This Service
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-xs" />
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Standards */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="standards" ref={setRef("standards")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("standards", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Our Commitment
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Care Standards You Can Trust
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Beyond our core services, we uphold the highest standards in daily wellness, safety, and privacy.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {serviceStandards.map((standard, i) => (
              <div
                key={standard.title}
                className={`bg-white rounded-xl p-6 border border-gray-100 hover:border-brand-green-200 hover:shadow-md transition-all duration-300 ${
                  isVisible["standards"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${standard.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{standard.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{standard.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="how" ref={setRef("how")}>
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <div className={animateClass("how", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Simple Process
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              How to Get Started
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Getting the care you need is simple, straightforward, and starts with a single conversation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                step: "1",
                title: "Free Consultation",
                description: "Call or fill out our form to schedule a no-obligation care assessment with our team.",
                icon: "ri-phone-line",
              },
              {
                step: "2",
                title: "Care Assessment",
                description: "We evaluate your needs, preferences, and schedule to design a personalized care plan.",
                icon: "ri-file-list-3-line",
              },
              {
                step: "3",
                title: "Caregiver Match",
                description: "We carefully match you with a caregiver whose skills and personality fit your needs.",
                icon: "ri-user-heart-line",
              },
              {
                step: "4",
                title: "Ongoing Support",
                description: "We regularly check in, adjust care as needed, and provide 24/7 on-call support.",
                icon: "ri-24-hours-line",
              },
            ].map((item, i) => (
              <div
                key={item.step}
                className={`text-center ${
                  isVisible["how"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                } transition-all duration-500`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="relative inline-block mb-4">
                  <div className="w-16 h-16 rounded-full bg-brand-green-50 flex items-center justify-center mx-auto">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${item.icon} text-xl text-brand-green-600`} />
                    </div>
                  </div>
                  <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-brand-green-600 text-white text-xs font-bold flex items-center justify-center">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Link Banner */}
      <section className="py-10 md:py-14 bg-soft-gray border-t border-gray-100">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
          <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Have Questions About Our Services?
                </h3>
                <p className="text-sm text-gray-500">
                  Find answers to common questions about coverage, scheduling, caregivers, and more.
                </p>
              </div>
              <Link
                to="/faq"
                className="shrink-0 inline-flex items-center gap-2 px-6 py-2.5 bg-brand-green-600 text-white text-sm font-semibold rounded-full hover:bg-brand-green-700 transition-all whitespace-nowrap"
              >
                Visit FAQ
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Not Sure What You Need?
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Our care advisors are here to help you understand your options and find the perfect care plan for your family. No pressure, just honest guidance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Schedule Free Consultation
            </Link>
            <Link
              to="/care-plans"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              View Care Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}